import { useEffect, useState } from 'react';

const POLL_INTERVAL_MS = 60_000;

function getModuleScriptSources(doc: Document): string {
  return Array.from(doc.querySelectorAll('script[type="module"][src]'))
    .map(script => (script.getAttribute('src') ?? '').split('?')[0])
    .filter(src => src && !src.includes('/@vite/'))
    .sort()
    .join('|');
}

async function fetchDeployedScriptSources(): Promise<string | null> {
  const response = await fetch(`/index.html?t=${Date.now()}`, { cache: 'no-store' });
  if (!response.ok) {
    return null;
  }

  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return getModuleScriptSources(doc);
}

/**
 * True when a newly deployed index.html points at a different JS bundle
 * than the one this tab originally loaded.
 */
export function useAppUpdateCheck(): boolean {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    if (updateAvailable) {
      return;
    }

    const loadedSources = getModuleScriptSources(document);
    if (!loadedSources) {
      return;
    }

    let cancelled = false;

    const check = async () => {
      try {
        const deployedSources = await fetchDeployedScriptSources();
        if (!cancelled && deployedSources && deployedSources !== loadedSources) {
          setUpdateAvailable(true);
        }
      } catch {
        // Ignore transient network errors; the next poll will retry.
      }
    };

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void check();
      }
    }, POLL_INTERVAL_MS);

    const onBecomeVisible = () => {
      if (document.visibilityState === 'visible') {
        void check();
      }
    };

    document.addEventListener('visibilitychange', onBecomeVisible);
    window.addEventListener('focus', onBecomeVisible);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onBecomeVisible);
      window.removeEventListener('focus', onBecomeVisible);
    };
  }, [updateAvailable]);

  return updateAvailable;
}
